import { UniAgFns } from "@/shared/uni-ag-fns";
import { ColDef, GridApi, RowSelectionOptions } from "@ag-grid-community/core";
import { Component } from "@angular/core";
import { ProductType, Product, Manufacturer } from "app/model/entities/entity-model";
import { AgFns, ISortModel, RowSelectionSingle } from "app/shared/ag-fns";
import { DbQueryService } from "app/shared/db-query.service";
import { EntityFinderDialog, EntityFinderParams } from "app/shared/entity-finder.dialog";
import { UtilFns } from "app/shared/util-fns";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";

export interface ProductFinderParams extends EntityFinderParams {
  // only one or the other should be set
  // if manufacturer is set - the base query is changed
  // if productType is set only the filter is changed
  manufacturer?: Manufacturer;
  productType?: ProductType;
  rowSelection?: RowSelectionOptions;
}

@Component({
  template: EntityFinderDialog.HTML,
  host: { hostID: crypto.randomUUID().toString() },
  standalone: false,
})
export class ProductFinderDialogComponent extends EntityFinderDialog<Product, ProductFinderParams> {
  targetProductType: ProductType;
  targetManufacturer: Manufacturer;

  constructor(ref: DynamicDialogRef, config: DynamicDialogConfig<ProductFinderParams>, public dbQueryService: DbQueryService) {
    super(ref, config);

    this.targetProductType = this.params.productType;
    this.targetManufacturer = this.params.manufacturer;

    const rowSelection = this.params.rowSelection ?? RowSelectionSingle;
    const suffix = rowSelection.mode == "multiRow" ? "s" : "";

    Object.assign(this.config, {
      header: `Select Product` + suffix,
      width: "800px",
      height: "600px",
      rowModelType: "serverSide",
    });
  }

  override async initDatasource() {
    let q = this.dbQueryService.createProductQuery();
    if (this.targetManufacturer != null) {
      q = q.where({ "productType.manufacturerId": this.targetManufacturer.id });
    }
    const ds = AgFns.buildDatasource(() => q);
    return ds;
  }

  override getColDefsAndSortModel() {
    const colDefs = [
      { ...UniAgFns.getManufacturerColDef("productType.manufacturer.name") },
      { ...UniAgFns.getProductTypeSKUColDef("productType.style") },
      { ...UniAgFns.getProductTypeDescriptionColDef("productType.description") },
      { ...UniAgFns.getFeatureChoicesColDef("featureChoicesExtract") },
    ];
    if (this.targetManufacturer == null) {
      colDefs[0].filter = "agTextColumnFilter";
    }
    const sortModel = [
      { colId: "productType.manufacturer.name", sort: "asc" },
      { colId: "productType.description", sort: "asc" },
      { colId: "productType.style", sort: "asc" },
    ] as ISortModel;
    return [colDefs, sortModel] as const;
  }

  override async processGrid(gridApi: GridApi) {
    if (this.targetProductType == null) {
      return;
    }

    gridApi.setColumnFilterModel("productType.manufacturer.name", {
      filterType: "text",
      type: "equals",
      filter: this.targetProductType.manufacturer.name,
    });
    gridApi.setColumnFilterModel("productType.style", {
      filterType: "text",
      type: "equals",
      filter: this.targetProductType.style,
    });

    gridApi.onFilterChanged();
    await UtilFns.wait(0);
  }
}
